// @ts-nocheck
import React, { FC, useEffect, useMemo, useState } from "react";
import { PageProps } from "../Page.model";
import {
  IonButton,
  IonCol,
  IonContent,
  IonGrid,
  IonImg,
  IonItem,
  IonPage,
  IonRow,
  IonText,
  IonToast,
  IonProgressBar,
} from "@ionic/react";
import { PaymentInfo, WrapperFilterSort } from "../../components";
import CurrencyFormat from 'react-currency-format';
import './Bills.css'
import Upload from "../Settings/Upload";
import { BillsStatus, SortProps, SortPropsTitle } from "./Bills.model";
import ArrowRightIcon from "../../assets/arrow-right-bills.svg";
import { format } from "date-fns";
import { OptionProps } from "../../components/WrapperFilterSort/WrapperFilterSort.model";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { invoiceToApproveAdminState, invoiceToApproveState, userState, alertState } from "../../atoms";
import AdminBillApproval from "./AdminBillApproval";
import InvoiceApproval from "./InvoiceApproval";
import { whichAuth as getAuth } from "../../getAuth";
import { handleErrors } from "../../utils/handleErrors";
import { REST_API_URL } from "../../constants/global";

const configSort: Record<SortPropsTitle, { [key: string]: SortProps, initialValue: SortProps }> = {
  [ SortPropsTitle.Provider] :
    {
      [SortProps.ProviderAsc]: SortProps.ProviderDesc,
      [SortProps.ProviderDesc]: SortProps.ProviderAsc,
      initialValue: SortProps.ProviderAsc
    },
  [ SortPropsTitle.Amount] :
    {
      [SortProps.AmountAsc]: SortProps.AmountDesc,
      [SortProps.AmountDesc]: SortProps.AmountAsc,
      initialValue: SortProps.AmountAsc
    },
  [ SortPropsTitle.Due] :
    {
      [SortProps.DueAsc]: SortProps.DueDesc,
      [SortProps.DueDesc]: SortProps.DueAsc,
      initialValue: SortProps.DueAsc
    },
  [ SortPropsTitle.Status] :
    {
      [SortProps.StatusAsc]: SortProps.StatusDesc,
      [SortProps.StatusDesc]: SortProps.StatusAsc,
      initialValue: SortProps.StatusAsc
    },
}

const Bills: FC<PageProps> = ({ show } ) => {
  const user = useRecoilValue(userState);
  const setUser = useSetRecoilState(userState);
  const invoiceToApprove = useRecoilValue(invoiceToApproveState);
  const setInvoiceToApprove = useSetRecoilState(invoiceToApproveState);
  const setInvoiceToApproveAdmin = useSetRecoilState(invoiceToApproveAdminState);
  const setAlertData = useSetRecoilState(alertState);

  const [searchFilter, setSearchFilter] = useState<string>('');
  const [sort, setSort] = useState<OptionProps | null>(null)
  const [options, setOptions] = useState<OptionProps[]>([
    { value: SortProps.ProviderAsc, title: SortPropsTitle.Provider },
    { value: SortProps.AmountAsc, title: SortPropsTitle.Amount },
    { value: SortProps.DueAsc, title: SortPropsTitle.Due },
    { value: SortProps.StatusAsc, title: SortPropsTitle.Status },
  ])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [data, setData] = useState([]);
  const [unapproveInvoiceId, setUnapproveInvoiceId] = useState(null);
  const [showUndoToast, setShowUndoToast] = useState<boolean>(false);

  const getInvoices = async () => {
    try {
      setIsLoading(true)
      const token = await getAuth()?.currentUser?.getIdToken()
      const invoicesResponse = await fetch(user?._links?.invoices?.href, {headers: {'Authorization': 'Bearer ' + token}})
      if (invoicesResponse.status === 200) {
        const invoices = (await invoicesResponse.json()).map(invoice => {
          return {
            ...invoice,
            total: (invoice.total/100).toFixed(2) // convert total in cents to currency amount with 2 decimals
          }
        })
        setData(invoices)
        if (user?.billsReceived && invoices.length && !user.admin) {
          setUser({ ...user, updateInvoice: false, billsReceivedList: invoices.filter((item) => item.status === 'USER_APPROVAL') })
        } else {
          setUser({ ...user, updateInvoice: false, billsReceived: false, billsReceivedList: null })
        }
      } else {
        throw new Error('Failed to retrieve your bills.')
      }
    } catch (error) {
      setAlertData({
        isOpen:true,
        title:'Failed to retrieve your bills'
      });
    } finally {
      setIsLoading(false)
    }
  }
  
  const approveInvoice = (invoiceId) => {
    const newData = data.filter((item) => item?.id !== invoiceId)
    updateInvoice(invoiceId, 'USER_APPROVAL')
      .then(() => {
        setUser({
          ...user,
          billsReceived: true,
          billsReceivedList: newData.filter((item) => item?.status !== 'PENDING_PAYMENT')
        })
        setUnapproveInvoiceId(invoiceId);
        setShowUndoToast(true);
      })
  };
  
  const unapproveInvoice = () => updateInvoice(unapproveInvoiceId, 'UNVERIFIED');

  const updateInvoice = (invoiceId, status) => getAuth()?.currentUser?.getIdToken().then(token =>
    fetch(REST_API_URL + '/invoices/' + invoiceId, {
      method: 'PATCH',
      body: JSON.stringify({status:status}),
      headers: {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/merge-patch+json'
      }
    })
    .then(handleErrors)
    .then(getInvoices)
    .catch(() => {
      setAlertData({
        isOpen:true,
        title:'Failed to update bill'
      });
    }));

  const payInvoice = invoiceId => getAuth()?.currentUser?.getIdToken().then(token =>
    fetch(REST_API_URL + '/invoices/' + invoiceId + '/pay', {
      method: 'POST',
      headers: {'Authorization': 'Bearer ' + token}
    })
    .then(response => {
      if(response.status === 402) {
        setAlertData({
          isOpen:true,
          title:'Unable to process credit card hold',
          subTitle:'User does not have any repayments yet. Due to Stripe, we can only take a hold once the first payment has been processed. Please retry tomorrow.'
        });
      } else {
        getInvoices();
      }
    })
    .catch(() => {
      setAlertData({
        isOpen:true,
        title:'Failed to mark invoice as paid'
      });
    }));
      
  const markHistorical = invoiceId => getAuth()?.currentUser?.getIdToken().then(token =>
    fetch(REST_API_URL + '/invoices/' + invoiceId + '/historical', {
      method: 'POST',
      headers: {'Authorization': 'Bearer ' + token}
    })
    .then(handleErrors)
    .then(getInvoices)
    .catch(() => {
      setAlertData({
        isOpen:true,
        title:'Failed to update invoice'
      });
    }));

  const deleteInvoice = invoiceId => getAuth()?.currentUser?.getIdToken().then(token => {
    fetch(REST_API_URL + '/invoices/' + invoiceId, {
      method: 'DELETE',
      headers: {'Authorization': 'Bearer ' + token}
    })
    .then(handleErrors)
    .then(() => {
      setInvoiceToApproveAdmin(null);
      getInvoices();
    })
    .catch(() => {
      setAlertData({
        isOpen:true,
        title:'Failed to delete invoice'
      });
    })
  })
  
  const billsFilter = useMemo(() => {
    let tmpArray = []
    return data.filter((item) => {
      if (!!item?.provider && !tmpArray.includes(item?.provider?.name)) {
        tmpArray.push(item?.provider?.name);
        return true
      }
      return false
    })
  }, [data])
    
  useEffect(() => {
    if (!!user?.updateInvoice && !invoiceToApprove) {
      getInvoices()
    }
  }, [invoiceToApprove, user?.updateInvoice])

  useEffect(() => {
   if (user?.billsReceived && user?.billsReceivedList?.length) {
      if (user?.admin) {
        setInvoiceToApproveAdmin(user?.billsReceivedList[0])
      } else {
        setInvoiceToApprove(user?.billsReceivedList[0])
      }
    } else {
     setUser({ ...user, billsReceivedList: null, billsReceived: false })
    }
  }, [user?.billsReceivedList, user?.billsReceived])

  const billsList = useMemo(() => {
    return searchFilter ? data?.filter((item) => item?.provider?.name?.toLowerCase().includes(searchFilter.toLowerCase())) : data
  }, [data, searchFilter])

  useEffect(() => {
    if (show) {
      getInvoices()
      setSort(null)
      setSearchFilter('')
    }
  }, [show]);

  useEffect(() => {
    if (!sort) return
    setOptions((state) => state.map((item) => {
      return {
        ...item,
        value: sort.title === item.title ? configSort[item.title][item.value] : configSort[item.title].initialValue ,
      }
    }))
  }, [sort])

  return show && (
    <IonPage>
      <PaymentInfo />
      {isLoading && !!billsList ? (
        <IonContent><IonProgressBar type="indeterminate" /></IonContent>
      ) : (
        <IonContent className="bills-content">
          <WrapperFilterSort
            configSort={configSort}
            setSearchFilter={(filterItem) => setSearchFilter(filterItem)}
            data={billsFilter}
            options={options}
            setSort={(sortItem) => setSort(sortItem)}
            sort={sort}
          />
          {!billsList.length && (
            <>
              <div className="settings-col-title no-available">No bills available.</div>
              <Upload />
            </>
          ) }
          {billsList?.sort((a, b) => {
              if (sort?.value === SortProps.ProviderDesc) {
                return a?.provider?.name.toLowerCase() > b?.provider?.name.toLowerCase() ? -1 : 1;
              } else if (sort?.value === SortProps.ProviderAsc) {
                return a?.provider?.name.toLowerCase() > b?.provider?.name.toLowerCase() ? 1 : -1;
              } else if (sort?.value === SortProps.AmountDesc) {
                return Number(a?.total) > Number(b?.total) ? -1 : 1;
              } else if (sort?.value === SortProps.AmountAsc) {
                return Number(a?.total) > Number(b?.total) ? 1 : -1;
              } else if (sort?.value === SortProps.StatusDesc) {
                return a?.status.toLowerCase() < b?.status.toLowerCase() ? -1 : 1;
              } else if (sort?.value === SortProps.StatusAsc) {
                return a?.status.toLowerCase() < b?.status.toLowerCase() ? 1 : -1;
              } else if (sort?.value === SortProps?.DueDesc) {
                a = new Date(a?.dueDate);
                b = new Date(b?.dueDate);
                return a > b ? -1 : 1;
              } else if (sort?.value === SortProps.DueAsc) {
                a = new Date(a?.dueDate);
                b = new Date(b?.dueDate);
                return a > b ? 1 : -1;
              }
            }
          )?.map((item) => (
            <IonItem
              button
              detail
              id={item?.id}
              key={item?.id}
              detailIcon={ArrowRightIcon}
              className="bills-content-item"
              onClick={() => user.admin ? setInvoiceToApproveAdmin(item) : setInvoiceToApprove(item)}
            >
              {item?.provider?.logo && (
                <IonImg src={item?.provider?.logo} className="bills-content-icon" />
              )}
              <IonGrid>
                <IonRow className="bills-content-item-wrapper">
                  <IonCol size="12" className="bills-content-header-wrapper">
                    <div>
                      <div className="bills-content-title">{item?.provider?.name}</div>
                      <IonText className="bills-content-subtitle">
                        <IonText className="bills-content-subtitle-hidden">{item?.provider?.service} | </IonText>
                        { user?.paymentOption !== 'MANUAL' && <>{BillsStatus[item?.status]} |</> }
                        Due {format(new Date(item?.dueDate), 'dd MMM')}
                      </IonText>
                    </div>
                    <div className="bills-content-header-wrapper review">
                      {item?.status === 'USER_APPROVAL' && (
                        <IonButton
                          className="bills-button-review"
                          onClick={() => user?.admin ? setInvoiceToApproveAdmin(item) : setInvoiceToApprove(item)}
                        >
                          Review{window.innerWidth > 600 && ' & Approve' }
                        </IonButton>
                      )}
                      <CurrencyFormat
                        value={item?.total}
                        thousandSeparator={true}
                        decimalScale={2}
                        fixedDecimalScale={true}
                        prefix='$'
                        displayType='text'
                        className="bills-content-title total"
                      />
                    </div>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonItem>
          ))}
        </IonContent>
      )}
      
      {!user?.admin ?
        <InvoiceApproval markHistorical={id => markHistorical(id)} />
        :
        <AdminBillApproval
          payInvoice={(id) => payInvoice(id)}
          approveInvoice={(id) => approveInvoice(id)}
          markHistorical={(id) => markHistorical(id)}
          deleteInvoice={(id) => deleteInvoice(id)}
          setData={(field, provider, invoiceId) => {
            setData(data.map(invoice => (invoice?.id !== invoiceId) ? invoice : {
              ...invoice,
              [field]: provider,
            }));
          }}
        />
      }
          
      <IonToast
        isOpen={showUndoToast}
        onDidDismiss={() => setShowUndoToast(false)}
        message="Bill approved."
        duration={5000}
        buttons={[
          {
            side: 'end',
            text: 'Undo approval',
            handler: unapproveInvoice
          }
        ]}
      />
    </IonPage>
  )
}

export default Bills
